/**
 * Hero view — the "index face" at the top of the portal.
 *
 * Renders the headline maturity number (`snapshot.maturity`), the delta since
 * the previous upload (`snapshot.deltaVsPrevious`), and the total case count
 * (`snapshot.total`).
 *
 * The DOM structure mirrors the prototype (`ai-maturity-index-portal_9.html`)
 * so a later CSS pass can port the styles wholesale:
 *
 *   <section class="hero">
 *     <div class="index-face">
 *       <div class="eyebrow">AI Maturity Index</div>
 *       <div class="index-value">…</div>
 *       <div class="index-delta {flat|up|down}">…</div>
 *       <div class="index-def">…</div>
 *       <div class="hero-total">{total} use cases</div>
 *     </div>
 *   </section>
 *
 * This module has no store subscription — the portal shell (task 7.7) drives
 * `render()` calls. Both views accept `snapshot === null` for the initial
 * boot state.
 *
 * Requirements: 10.1, 10.4, 10.6, 10.7.
 */
import type { IndexSnapshot } from "@aim/types";

/** Public surface of the hero view. */
export interface HeroView {
  /** Mount the hero shell into `container`. Idempotent — safe to call twice. */
  mount(container: HTMLElement): void;
  /** Render (or re-render) the current snapshot. Safe to call before mount is a no-op. */
  render(snapshot: IndexSnapshot | null): void;
  /** Unmount and release cached element references. */
  destroy(): void;
}

/** Placeholder text shown before any snapshot has been computed. */
const PLACEHOLDER_VALUE = "—";
const PLACEHOLDER_DELTA = "no cases yet";
const DEFINITION_TEXT =
  "The index equals the number of AI use cases in full production.";

interface HeroRefs {
  root: HTMLElement;
  value: HTMLElement;
  delta: HTMLElement;
  total: HTMLElement;
}

/**
 * Build the hero shell inside `container`, replacing any previous contents.
 * Returns cached references to the elements that `render()` updates.
 */
function buildShell(container: HTMLElement): HeroRefs {
  // Reset first — this is what makes mount idempotent. Anything the caller
  // left inside `container` (including a previous mount of this view) is
  // discarded.
  container.replaceChildren();

  const root = document.createElement("section");
  root.className = "hero";

  const face = document.createElement("div");
  face.className = "index-face";

  const eyebrow = document.createElement("div");
  eyebrow.className = "eyebrow";
  eyebrow.textContent = "AI Maturity Index";

  const value = document.createElement("div");
  value.className = "index-value";
  value.textContent = PLACEHOLDER_VALUE;

  const delta = document.createElement("div");
  delta.className = "index-delta flat";
  delta.textContent = PLACEHOLDER_DELTA;

  const definition = document.createElement("div");
  definition.className = "index-def";
  definition.textContent = DEFINITION_TEXT;

  const total = document.createElement("div");
  total.className = "hero-total";
  total.textContent = "";

  face.append(eyebrow, value, delta, definition, total);
  root.append(face);
  container.append(root);

  return { root, value, delta, total };
}

/** Factory — every call returns an independent view instance. */
export function createHeroView(): HeroView {
  let refs: HeroRefs | null = null;

  return {
    mount(container: HTMLElement): void {
      // Idempotent: mounting twice throws away the previous shell rather
      // than appending a second `.hero` element.
      refs = buildShell(container);
    },

    render(snapshot: IndexSnapshot | null): void {
      if (refs === null) {
        // Rendering before mount is a no-op; the shell will pick up state
        // when mounted next. Callers (the portal shell) always mount first,
        // but we keep this defensive so the store can fan-out unconditionally.
        return;
      }

      if (snapshot === null) {
        refs.value.textContent = PLACEHOLDER_VALUE;
        refs.delta.className = "index-delta flat";
        refs.delta.textContent = PLACEHOLDER_DELTA;
        refs.total.textContent = "";
        return;
      }

      refs.value.textContent = String(snapshot.maturity);

      const d = snapshot.deltaVsPrevious;
      if (d > 0) {
        refs.delta.className = "index-delta up";
        refs.delta.textContent = `▲ +${String(d)} since last upload`;
      } else if (d < 0) {
        refs.delta.className = "index-delta down";
        refs.delta.textContent = `▼ ${String(d)} since last upload`;
      } else {
        refs.delta.className = "index-delta flat";
        refs.delta.textContent = "— no change since last upload";
      }

      const total = snapshot.total;
      refs.total.textContent = `${String(total)} use case${total === 1 ? "" : "s"}`;
    },

    destroy(): void {
      if (refs !== null) {
        refs.root.remove();
        refs = null;
      }
    },
  };
}
